<template>
  <UiNarrowContainer>
    <CategorySkeleton v-if="status === 'pending'" centered />
    <div v-else-if="data?.category">
      <UiHeading type="d3" class="mb-5">{{ data?.category.name[locale as Locale] }}</UiHeading>
      <UiBreadcrumb />
      <div class="mt-10 flex flex-row gap-6">
        <div v-if="!isMobile" class="w-1/4">
          <ProductListFilters :is-loading="isLoading" />
        </div>
        <TransitionsSlide v-else direction="right">
          <SfDrawer
            ref="drawerRef"
            :model-value="isDrawerOpen"
            disable-click-away
            placement="top"
            class="z-30 flex max-h-screen min-h-screen flex-col items-start overflow-y-auto bg-white p-6"
          >
            <UiIconButton
              id="category-filter-close"
              :icon="IconClose"
              variant="secondary"
              class="absolute right-2 top-2"
              @click="isDrawerOpen = false"
            />
            <ProductListFilters :is-loading="isLoading" class="w-full" :close="() => (isDrawerOpen = false)" />
          </SfDrawer>
        </TransitionsSlide>
        <div class="w-full md:w-3/4">
          <ProductListSettings
            :is-loading="isLoading"
            :pagination="categoryStore.pagination"
            :sort-direction="categoryStore.currentProductSortDirection"
            :sort-by="categoryStore.currentProductSortBy"
            :toggle-sort-direction="categoryStore.toggleCurrentProductSortDirection"
            :set-sort-by="categoryStore.setCurrentProductSortBy"
          />
          <ProductList :is-loading="isLoading" :products="categoryStore.products ?? []" />
          <ProductListPagination
            :current-page="categoryStore.currentPage"
            :set-current-page="(page) => (categoryStore.currentPage = page)"
            :pagination="categoryStore.pagination"
          />
        </div>
      </div>
    </div>
  </UiNarrowContainer>
  <div
    v-if="isMobile && !isDrawerOpen"
    class="sticky bottom-0 flex flex-row gap-6 border-t border-neutral-300 bg-white p-6"
  >
    <UiButton
      id="category-clear-filters"
      :disabled="isLoading"
      variant="secondary"
      class="w-full"
      @click="clearFilters()"
    >
      {{ $t('category.clearFilters') }}
    </UiButton>
    <UiButton id="category-edit-filters" :disabled="isLoading" class="w-full" @click="isDrawerOpen = true">
      {{ $t('category.editFilters') }}
    </UiButton>
  </div>
</template>

<script setup lang="ts">
import { IconClose, TransitionsSlide } from '#components';
import { digest } from 'ohash';

import type { CategoryPageProps } from '~/components/Category/types';
import type { Locale } from '~/stores/app/types';
import type { Category } from '~/stores/category/types';
import type { Product } from '~/stores/product/types';

import { useAppStore } from '~/stores/app/appStore';
import { useAuthStore } from '~/stores/auth/authStore';
import { useCategoryStore } from '~/stores/category/categoryStore';
import { useCustomerStore } from '~/stores/customer/customerStore';

const route = useRoute();
const { locale } = useI18n();
const { isMobile } = useBreakpoints();
const runtimeConfig = useRuntimeConfig();
const {
  proxy: { dataLayer },
} = useScriptGoogleTagManager();

const isLoading = ref<boolean>(false);
const drawerRef = ref<HTMLElement>();
const isDrawerOpen = ref<boolean>(false);

const categoryStore = useCategoryStore();
const appStore = useAppStore();
const customerStore = useCustomerStore();
const authStore = useAuthStore();

const { currentFilters, currentPage, currentProductSortBy, currentProductSortDirection } = storeToRefs(categoryStore);

const lastSlug = computed(() => route.params?.slug?.[route.params?.slug.length - 1] as string | undefined);

const clearFilters = (): void => {
  categoryStore.setCurrentFilters(null);
};

const dataKey = computed(() => `category-${props.pageResolver.data.slug}-${props.pageResolver.data.locale}`);

const props = defineProps<CategoryPageProps>();

const { data, status } = await useAsyncData<{ category: Category | null; products: Product[] | null }>(
  dataKey,
  async () => {
    const resolvedSlug = props.pageResolver.data.slug;
    const resolvedLocale = props.pageResolver.data.locale as Locale;

    categoryStore.currentPage = 1;

    const [category, products] = await Promise.all([
      categoryStore.fetchCategory(resolvedSlug, resolvedLocale),
      categoryStore.fetchCategoryProducts(resolvedSlug, resolvedLocale),
    ]);

    return { category, products };
  },
);

watch([currentProductSortDirection, currentProductSortBy, currentPage, currentFilters], () => {
  if (!lastSlug.value) return;

  isLoading.value = true;

  categoryStore.fetchCategoryProducts(lastSlug.value, locale.value as Locale).finally(() => (isLoading.value = false));
});

useTrapFocus(drawerRef, {
  activeState: isDrawerOpen,
  arrowKeysUpDown: true,
  initialFocus: 'container',
});

onMounted(() => {
  dataLayer.push({
    country: appStore.currentCountryCode,
    event: 'page_view',
    hashed_email: customerStore.customer?.email ? digest(customerStore.customer?.email) : 'n/a',
    locale: locale.value,
    page_key: dataKey.value,
    user_logged: authStore.isLoggedIn,
  });
});

const title =
  categoryStore.category?.meta_title?.[locale.value as Locale] ?? categoryStore.category?.name[locale.value as Locale];
const description =
  categoryStore.category?.meta_description?.[locale.value as Locale] ??
  categoryStore.category?.description?.[locale.value as Locale];

useSeoMeta({
  description,
  ogDescription: description,
  ogImage: {
    url: `${runtimeConfig.public.baseUrl}/favicon.svg`,
  },
  ogSiteName: runtimeConfig.public.baseUrl,
  ogTitle: title,
  ogType: 'website',
  title,
  twitterCard: 'summary',
  twitterDescription: description,
  twitterImage: `${runtimeConfig.public.baseUrl}/favicon.svg`,
  twitterSite: runtimeConfig.public.xTwitterAccount,
  twitterTitle: title,
});
</script>
